import { subDays, isWithinInterval } from 'date-fns';
import type { Snap, SnapCategory, SnapActivity } from '@/types/database.types';

export const getCategoryColor = (category: SnapCategory) => {
  switch (category) {
    case 'politics': return {
      bg: 'bg-blue-100',
      text: 'text-blue-800',
      hover: 'hover:bg-blue-200',
      selected: 'bg-blue-600 text-white',
      icon: '🏛️'
    };
    case 'economy': return {
      bg: 'bg-green-100',
      text: 'text-green-800',
      hover: 'hover:bg-green-200',
      selected: 'bg-green-600 text-white',
      icon: '📈'
    };
    case 'finance': return {
      bg: 'bg-emerald-100',
      text: 'text-emerald-800',
      hover: 'hover:bg-emerald-200',
      selected: 'bg-emerald-600 text-white',
      icon: '💰'
    };
    case 'relationships': return {
      bg: 'bg-pink-100',
      text: 'text-pink-800',
      hover: 'hover:bg-pink-200',
      selected: 'bg-pink-600 text-white',
      icon: '❤️'
    };
    case 'lifestyle': return {
      bg: 'bg-purple-100',
      text: 'text-purple-800',
      hover: 'hover:bg-purple-200',
      selected: 'bg-purple-600 text-white',
      icon: '🌟'
    };
    case 'technology': return {
      bg: 'bg-indigo-100',
      text: 'text-indigo-800',
      hover: 'hover:bg-indigo-200',
      selected: 'bg-indigo-600 text-white',
      icon: '💻'
    };
    case 'health': return {
      bg: 'bg-red-100',
      text: 'text-red-800',
      hover: 'hover:bg-red-200',
      selected: 'bg-red-600 text-white',
      icon: '🏥'
    };
    case 'society_ethics': return {
      bg: 'bg-amber-100',
      text: 'text-amber-800',
      hover: 'hover:bg-amber-200',
      selected: 'bg-amber-600 text-white',
      icon: '⚖️'
    };
    case 'entertainment': return {
      bg: 'bg-orange-100',
      text: 'text-orange-800',
      hover: 'hover:bg-orange-200',
      selected: 'bg-orange-600 text-white',
      icon: '🎮'
    };
    case 'personal_growth': return {
      bg: 'bg-cyan-100',
      text: 'text-cyan-800',
      hover: 'hover:bg-cyan-200',
      selected: 'bg-cyan-600 text-white',
      icon: '🎯'
    };
    default: return {
      bg: 'bg-gray-100',
      text: 'text-gray-800',
      hover: 'hover:bg-gray-200',
      selected: 'bg-gray-900 text-white',
      icon: '📊'
    };
  };
};

export const getActivityThresholds = (snaps: Snap[]) => {
  if (snaps.length === 0) return { hot: 0, trending: 0 };

  // Calculate activity scores for each snap
  const scores = snaps.map(snap => {
    // Weighted score calculation:
    // - Votes are weighted most heavily (2x)
    // - Likes are weighted moderately (1.5x)
    // - Comments are weighted highly (3x)
    // - Views are weighted least (1x)
    const votes = snap.total_votes * 2;
    const likes = snap.likes_count * 1.5;
    const comments = snap.commentCount * 3;
    const views = snap.view_count * 1;
    
    return votes + likes + comments + views;
  }).sort((a, b) => b - a); // Sort in descending order

  // Define thresholds based on percentiles
  // - Hot: Top 5% of SNAPs
  // - Trending: Top 10% of SNAPs
  const topFivePercent = Math.max(Math.floor(scores.length * 0.05), 1);
  const topTenPercent = Math.max(Math.floor(scores.length * 0.1), 1);

  return {
    hot: scores[topFivePercent - 1] || 0,
    trending: scores[topTenPercent - 1] || 0
  };
};

export const getSnapActivity = (snap: Snap, thresholds: { hot: number; trending: number }): SnapActivity => {
  // First check if it's a new SNAP (created within last 24 hours)
  const now = new Date();
  const snapDate = new Date(snap.created_at);
  const isNew = isWithinInterval(snapDate, {
    start: subDays(now, 1),
    end: now
  });

  if (isNew) return 'new';

  // Calculate activity score
  const votes = snap.total_votes * 2;
  const likes = snap.likes_count * 1.5;
  const comments = snap.commentCount * 3;
  const views = snap.view_count * 1;
  const score = votes + likes + comments + views;

  if (score >= thresholds.hot) return 'hot';
  if (score >= thresholds.trending) return 'trending';
  return null;
};